import React, { useEffect, useState, useCallback } from 'react';
import ReactFlow, {
    Background,
    Controls,
    MiniMap,
    ReactFlowProvider,
    MarkerType,
    useNodesState,
    useEdgesState,
} from 'reactflow';
import { Spin, Typography, Button, Alert, Collapse, Tag, message as antdMessage } from 'antd';
import { RobotOutlined, ExpandOutlined, CompressOutlined, CalendarOutlined, UndoOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import 'reactflow/dist/style.css';
import './ModuleGroupPreview.css';

import ModuleNode from './ModuleNode';
import IconEdge from './IconEdge';
import InterviewMessageInput from '../interview-invites/InterviewMessageInput';
import apiCore from '../../utils/api-core';
import layoutModules from '../../utils/flow-layout';
import { findInvalidMergeTokens, formatInvalidTokenMessage } from '../../utils/mailmerge';

const { Text, Title } = Typography;

// Reuse the same node & edge types
const nodeTypes = { moduleNode: ModuleNode };
const edgeTypes = { iconEdge: IconEdge };

const TRIGGER_EDGE_COLORS = {
    no_reply_timeout: '#f5222d',
    scheduled: '#722ed1',
    ats_status_change: '#fa541c',
};
const TRIGGER_LABELS = {
    no_reply_timeout: (config) => `timeout ${(config || {}).timeout_minutes || 60}m`,
    scheduled: (config) => {
        const dm = (config || {}).delay_minutes || 60;
        if (dm >= 1440 && dm % 1440 === 0) return `delay ${dm / 1440}d`;
        if (dm >= 60 && dm % 60 === 0) return `delay ${dm / 60}h`;
        return `delay ${dm}m`;
    },
    ats_status_change: (config) => `ATS → ${(config || {}).to_status || '?'}`,
};
const TRIGGER_ICONS = {
    no_reply_timeout: 'clock',
    scheduled: 'calendar',
    ats_status_change: 'swap',
};

/**
 * Build edges array from modules.
 */
function buildEdges(modules, positions) {
    const flowEdges = [];
    modules.forEach((mod) => {
        const sourcePos = positions[mod.id];
        (mod.connection || []).forEach((conn) => {
            const isSelfLoop = mod.id === conn.goto;
            const targetPos = positions[conn.goto];
            const isBackEdge = !isSelfLoop && sourcePos && targetPos && targetPos.x <= sourcePos.x;

            flowEdges.push({
                id: `conn-${mod.id}-${conn.goto}`,
                source: mod.id,
                target: conn.goto,
                sourceHandle: `conn-${conn.goto}`,
                targetHandle: isSelfLoop ? 'self-target' : undefined,
                label: conn.filters,
                type: 'iconEdge',
                animated: false,
                style: { stroke: '#52c41a', strokeWidth: 2, strokeDasharray: '6,3' },
                markerEnd: { type: MarkerType.ArrowClosed, color: '#52c41a' },
                labelStyle: { fontSize: 10, fontWeight: 500 },
                labelBgStyle: { fill: 'rgba(255,255,255,0.9)', fillOpacity: 0.9 },
                labelBgPadding: [4, 2],
                labelBgBorderRadius: 3,
                data: { icon: 'filter', iconColor: '#52c41a', smoothstep: isSelfLoop || isBackEdge },
            });
        });

        if (mod.fallback) {
            flowEdges.push({
                id: `fallback-${mod.id}-${mod.fallback}`,
                source: mod.id,
                target: mod.fallback,
                sourceHandle: 'fallback',
                label: 'fallback',
                type: 'iconEdge',
                animated: false,
                style: { stroke: '#faad14', strokeWidth: 2, strokeDasharray: '5,5' },
                markerEnd: { type: MarkerType.ArrowClosed, color: '#faad14' },
                labelStyle: { fontSize: 10, fontStyle: 'italic' },
                labelBgStyle: { fill: 'rgba(255,255,255,0.9)', fillOpacity: 0.9 },
                labelBgPadding: [4, 2],
                labelBgBorderRadius: 3,
                data: { icon: 'warning', iconColor: '#faad14' },
            });
        }

        // Trigger edges
        const triggers = (mod.triggers || []).filter((t) => !t.deleted_at);
        triggers.forEach((trigger) => {
            if (!trigger.goto_module_id) return;
            const isSelfLoop = mod.id === trigger.goto_module_id;
            const targetPos = positions[trigger.goto_module_id];
            const isBackEdge = !isSelfLoop && sourcePos && targetPos && targetPos.x <= sourcePos.x;

            const edgeColor = TRIGGER_EDGE_COLORS[trigger.trigger_type] || '#cf1322';
            const labelFn = TRIGGER_LABELS[trigger.trigger_type];

            flowEdges.push({
                id: `trigger-${trigger.id}`,
                source: mod.id,
                target: trigger.goto_module_id,
                sourceHandle: `trigger-${trigger.id}`,
                targetHandle: isSelfLoop ? 'self-target' : undefined,
                label: labelFn ? labelFn(trigger.config) : trigger.trigger_type,
                type: 'iconEdge',
                animated: true,
                style: { stroke: edgeColor, strokeWidth: 2, strokeDasharray: '4,4' },
                markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor },
                labelStyle: { fontSize: 10, fontWeight: 500 },
                labelBgStyle: { fill: 'rgba(255,255,255,0.9)', fillOpacity: 0.9 },
                labelBgPadding: [4, 2],
                labelBgBorderRadius: 3,
                data: { icon: TRIGGER_ICONS[trigger.trigger_type] || 'clock', iconColor: edgeColor, smoothstep: isSelfLoop || isBackEdge },
            });
        });
    });
    return flowEdges;
}

// ── Inner component (needs ReactFlowProvider ancestor) ──
function ModuleGroupPreviewInner({
    moduleGroupId,
    snapshotData,
    moduleResponseOverrides,
    onOverridesChange,
    calendarRequired,
    userHasCalendar,
    noSlotsMessage,
    onNoSlotsMessageChange,
    onCalendarConnect,
    extraValidMergeTokens,
    // 'inline' (default) renders the flow preview inline above the accordion.
    // 'modal' hides the inline preview entirely; the parent supplies a button
    // that toggles `flowModalOpen`, and the existing maximize overlay handles
    // rendering. The Customize Messages accordion is unaffected.
    flowPreviewMode = 'inline',
    flowModalOpen = false,
    onFlowModalClose,
}) {
    const [loading, setLoading] = useState(false);
    const [moduleGroup, setModuleGroup] = useState(null);
    const [modules, setModules] = useState([]);
    const [maximized, setMaximized] = useState(false);
    const [hoveredNodeId, setHoveredNodeId] = useState(null);

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    const isInteractive = !!onOverridesChange;
    const [layoutPositions, setLayoutPositions] = useState({});

    // Use snapshot data if available, otherwise fetch from API
    useEffect(() => {
        if (snapshotData) {
            setModuleGroup(snapshotData);
            setModules(snapshotData.modules || []);
            setLoading(false);
            return;
        }

        if (!moduleGroupId) {
            setModuleGroup(null);
            setModules([]);
            return;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await apiCore.get(`/api/v3/module-group/${moduleGroupId}`);
                const data = res.data || res;
                if (!cancelled) {
                    setModuleGroup(data);
                    setModules(data.modules || []);
                }
            } catch (err) {
                console.error('Failed to fetch module group for preview:', err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [moduleGroupId, snapshotData]);

    // Editable modules sorted by flow position (left-to-right, top-to-bottom)
    const editableModules = modules
        .filter((m) => m.response_user_modify)
        .sort((a, b) => {
            const posA = layoutPositions[a.id] || { x: 0, y: 0 };
            const posB = layoutPositions[b.id] || { x: 0, y: 0 };
            return posA.x - posB.x || posA.y - posB.y;
        });

    // Track in-progress edits per module (not yet saved to overrides)
    const [drafts, setDrafts] = useState({});
    const [noSlotsDraft, setNoSlotsDraft] = useState(undefined);

    // Reset drafts when module group changes
    useEffect(() => { setDrafts({}); setNoSlotsDraft(undefined); }, [moduleGroupId]);

    // Save a single module's draft to overrides
    const handleSave = useCallback((moduleId, defaultResponse) => {
        if (!onOverridesChange) return;
        const value = drafts[moduleId];
        if (value === undefined) return; // nothing to save
        const next = { ...(moduleResponseOverrides || {}) };
        if (value === defaultResponse) {
            delete next[moduleId];
            antdMessage.success('Reset to default');
        } else {
            next[moduleId] = value;
            antdMessage.success('Message saved');
        }
        onOverridesChange(next);
        setDrafts((prev) => { const d = { ...prev }; delete d[moduleId]; return d; });
    }, [onOverridesChange, moduleResponseOverrides, drafts]);

    // Reset a single override back to default
    const handleOverrideReset = useCallback((moduleId) => {
        if (!onOverridesChange) return;
        const next = { ...(moduleResponseOverrides || {}) };
        delete next[moduleId];
        onOverridesChange(next);
        setDrafts((prev) => { const d = { ...prev }; delete d[moduleId]; return d; });
        antdMessage.success('Reset to default');
    }, [onOverridesChange, moduleResponseOverrides]);

    // Update draft value for a module
    const handleDraftChange = useCallback((moduleId, value) => {
        setDrafts((prev) => ({ ...prev, [moduleId]: value }));
    }, []);

    // Build nodes & edges when modules change
    useEffect(() => {
        if (!modules.length) {
            setNodes([]);
            setEdges([]);
            return;
        }

        const startModuleId = moduleGroup ? moduleGroup.module_id : null;
        const positions = layoutModules(modules, startModuleId);
        setLayoutPositions(positions);

        // Use saved positions from flow_meta if available
        const savedPositions = moduleGroup?.flow_meta?.nodes?.reduce((acc, n) => {
            acc[n.id] = n.position;
            return acc;
        }, {}) || {};

        const flowNodes = modules.map((mod) => {
            const overrideValue = (moduleResponseOverrides || {})[mod.id];
            return {
                id: mod.id,
                type: 'moduleNode',
                position: savedPositions[mod.id] || positions[mod.id] || { x: 100, y: 100 },
                data: {
                    module: mod,
                    isStartModule: mod.id === startModuleId,
                    isEditable: isInteractive && !!mod.response_user_modify,
                    isSelected: false,
                    displayResponse: overrideValue !== undefined ? overrideValue : mod.response,
                },
            };
        });

        const flowEdges = buildEdges(modules, positions);

        setNodes(flowNodes);
        setEdges(flowEdges);
    }, [modules, moduleGroup, isInteractive, moduleResponseOverrides, setNodes, setEdges]);

    // Update edge animation on node hover
    useEffect(() => {
        setEdges((eds) =>
            eds.map((e) => ({
                ...e,
                animated: hoveredNodeId ? (e.source === hoveredNodeId || e.target === hoveredNodeId) : false,
            }))
        );
    }, [hoveredNodeId, setEdges]);

    // Node hover handlers for edge animation
    const handleNodeMouseEnter = useCallback((_event, node) => {
        setHoveredNodeId(node.id);
    }, []);

    const handleNodeMouseLeave = useCallback(() => {
        setHoveredNodeId(null);
    }, []);

    // Empty state
    if (!moduleGroupId) {
        return (
            <div className="mg-preview-empty">
                <RobotOutlined />
                <Text type="secondary" style={{ fontSize: 13 }}>Select an agent to preview its flow</Text>
            </div>
        );
    }

    // Loading state
    if (loading) {
        return (
            <div className="mg-preview-loading">
                <Spin tip="Loading flow..." />
            </div>
        );
    }

    // No modules
    if (!modules.length) {
        return (
            <div className="mg-preview-empty">
                <RobotOutlined />
                <Text type="secondary" style={{ fontSize: 13 }}>This agent has no modules yet</Text>
            </div>
        );
    }

    const flowProps = {
        nodes,
        edges,
        nodeTypes,
        edgeTypes,
        onNodesChange,
        onEdgesChange,
        nodesDraggable: true,
        nodesConnectable: false,
        elementsSelectable: false,
        panOnDrag: true,
        zoomOnScroll: false,
        zoomOnPinch: false,
        preventScrolling: false,
        fitView: true,
        fitViewOptions: { padding: 0.3 },
        minZoom: 0.1,
        maxZoom: 1.5,
        proOptions: { hideAttribution: true },
        onNodeMouseEnter: handleNodeMouseEnter,
        onNodeMouseLeave: handleNodeMouseLeave,
    };

    // Build accordion items for editable modules
    const accordionItems = editableModules.map((mod) => {
        const overrideValue = (moduleResponseOverrides || {})[mod.id];
        const savedValue = overrideValue !== undefined ? overrideValue : (mod.response || '');
        const draftValue = drafts[mod.id];
        const displayValue = draftValue !== undefined ? draftValue : savedValue;
        const isOverridden = overrideValue !== undefined && overrideValue !== mod.response;
        const hasDraft = draftValue !== undefined && draftValue !== savedValue;
        const errorTokens = findInvalidMergeTokens(displayValue, extraValidMergeTokens);
        const errorMessage = formatInvalidTokenMessage(errorTokens);

        return {
            key: mod.id,
            className: errorTokens.length ? 'has-merge-error' : undefined,
            label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <Tag color={errorTokens.length ? 'red' : 'green'} style={{ margin: 0, flexShrink: 0 }}>{mod.title}</Tag>
                    <Text type="secondary" ellipsis style={{ fontSize: 13, flex: 1, minWidth: 0 }}>
                        {displayValue}
                    </Text>
                    {errorTokens.length > 0 && (
                        <span style={{ fontSize: 12, color: '#ff4d4f', fontWeight: 500, flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <ExclamationCircleFilled />
                            Invalid merge field{errorTokens.length > 1 ? 's' : ''}
                        </span>
                    )}
                    {isOverridden && errorTokens.length === 0 && (
                        <span style={{ fontSize: 12, color: '#4CAF50', fontWeight: 500, flexShrink: 0 }}>
                            (customized)
                        </span>
                    )}
                </span>
            ),
            children: (
                <div>
                    <InterviewMessageInput
                        value={displayValue}
                        onChange={(e) => handleDraftChange(mod.id, e.target.value)}
                        rows={3}
                        placeholder="Edit message..."
                        showAppend={!!mod.response_append}
                        appendContent={mod.response_append || ''}
                        errorMessage={errorMessage}
                    />
                    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            {isOverridden && (
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<UndoOutlined />}
                                    onClick={() => handleOverrideReset(mod.id)}
                                    style={{ padding: 0 }}
                                >
                                    Reset to default
                                </Button>
                            )}
                        </div>
                        <Button
                            type="primary"
                            size="small"
                            disabled={!hasDraft}
                            onClick={() => handleSave(mod.id, mod.response)}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            ),
        };
    });

    // Read-only override items for the campaign viewer (not interactive mode)
    const readOnlyOverrideItems = (!isInteractive && moduleResponseOverrides)
        ? modules
            .filter((m) => m.response_user_modify && moduleResponseOverrides[m.id] !== undefined)
            .sort((a, b) => {
                const posA = layoutPositions[a.id] || { x: 0, y: 0 };
                const posB = layoutPositions[b.id] || { x: 0, y: 0 };
                return posA.x - posB.x || posA.y - posB.y;
            })
            .map((mod) => {
                const overrideValue = moduleResponseOverrides[mod.id];
                return {
                    key: mod.id,
                    label: (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                            <Tag color="green" style={{ margin: 0, flexShrink: 0 }}>{mod.title}</Tag>
                            <Text type="secondary" ellipsis style={{ fontSize: 13, flex: 1, minWidth: 0 }}>
                                {overrideValue}
                            </Text>
                            <span style={{ fontSize: 12, color: '#4CAF50', fontWeight: 500, flexShrink: 0 }}>
                                (customized)
                            </span>
                        </span>
                    ),
                    children: (
                        <div>
                            <div style={{ padding: '8px 12px', background: 'var(--gh-gray-50, #FAFAFA)', borderRadius: 6, fontSize: 13, whiteSpace: 'pre-wrap' }}>
                                {overrideValue}
                            </div>
                            {mod.response_original && (
                                <div style={{ marginTop: 8 }}>
                                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Default message:</Text>
                                    <div style={{ padding: '8px 12px', background: 'var(--gh-gray-50, #FAFAFA)', borderRadius: 6, fontSize: 13, whiteSpace: 'pre-wrap', opacity: 0.7 }}>
                                        {mod.response_original}
                                    </div>
                                </div>
                            )}
                        </div>
                    ),
                };
            })
        : [];

    // Add no-slots message to read-only overrides (viewer mode)
    if (!isInteractive && noSlotsMessage) {
        readOnlyOverrideItems.push({
            key: '_no-slots-message',
            label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <Tag color="blue" style={{ margin: 0, flexShrink: 0 }}><CalendarOutlined style={{ marginRight: 4 }} />No Slots</Tag>
                    <Text type="secondary" ellipsis style={{ fontSize: 13, flex: 1, minWidth: 0 }}>
                        {noSlotsMessage}
                    </Text>
                    <span style={{ fontSize: 12, color: '#4CAF50', fontWeight: 500, flexShrink: 0 }}>
                        (customized)
                    </span>
                </span>
            ),
            children: (
                <div>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                        Sent when no calendar slots are available for scheduling.
                    </Text>
                    <div style={{ padding: '8px 12px', background: 'var(--gh-gray-50, #FAFAFA)', borderRadius: 6, fontSize: 13, whiteSpace: 'pre-wrap' }}>
                        {noSlotsMessage}
                    </div>
                </div>
            ),
        });
    }

    // Add "No Slots Message" item if calendar is active
    if (calendarRequired && userHasCalendar && onNoSlotsMessageChange) {
        const noSlotsDisplay = noSlotsDraft !== undefined ? noSlotsDraft : (noSlotsMessage || '');
        const noSlotsHasDraft = noSlotsDraft !== undefined && noSlotsDraft !== (noSlotsMessage || '');
        const noSlotsErrorTokens = findInvalidMergeTokens(noSlotsDisplay, extraValidMergeTokens);
        const noSlotsErrorMessage = formatInvalidTokenMessage(noSlotsErrorTokens);

        accordionItems.push({
            key: '_no-slots-message',
            className: noSlotsErrorTokens.length ? 'has-merge-error' : undefined,
            label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <Tag color={noSlotsErrorTokens.length ? 'red' : 'blue'} style={{ margin: 0, flexShrink: 0 }}><CalendarOutlined style={{ marginRight: 4 }} />No Slots</Tag>
                    <Text type="secondary" ellipsis style={{ fontSize: 13, flex: 1, minWidth: 0 }}>
                        {noSlotsDisplay}
                    </Text>
                    {noSlotsErrorTokens.length > 0 && (
                        <span style={{ fontSize: 12, color: '#ff4d4f', fontWeight: 500, flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <ExclamationCircleFilled />
                            Invalid merge field{noSlotsErrorTokens.length > 1 ? 's' : ''}
                        </span>
                    )}
                    {noSlotsMessage && noSlotsErrorTokens.length === 0 && (
                        <span style={{ fontSize: 12, color: '#4CAF50', fontWeight: 500, flexShrink: 0 }}>
                            (customized)
                        </span>
                    )}
                </span>
            ),
            children: (
                <div>
                    <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
                        Sent when no calendar slots are available for scheduling.
                    </Text>
                    <InterviewMessageInput
                        value={noSlotsDisplay}
                        onChange={(e) => setNoSlotsDraft(e.target.value)}
                        rows={2}
                        placeholder="Message when no calendar slots are available..."
                        showTemplates={false}
                        errorMessage={noSlotsErrorMessage}
                    />
                    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            type="primary"
                            size="small"
                            disabled={!noSlotsHasDraft}
                            onClick={() => {
                                onNoSlotsMessageChange(noSlotsDraft);
                                setNoSlotsDraft(undefined);
                                antdMessage.success('Message saved');
                            }}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            ),
        });
    }

    const isModalMode = flowPreviewMode === 'modal';
    const overlayOpen = isModalMode ? !!flowModalOpen : maximized;
    const closeOverlay = isModalMode ? (onFlowModalClose || (() => {})) : (() => setMaximized(false));

    return (
        <>
            {!isModalMode && (
                <div className="mg-preview-wrapper">
                    <span className="mg-preview-label">Agent Flow Preview</span>
                    <Button
                        type="text"
                        icon={<ExpandOutlined />}
                        className="mg-preview-maximize-btn"
                        onClick={() => setMaximized(true)}
                        title="Maximize"
                    />
                    <ReactFlow {...flowProps}>
                        <Background gap={15} size={1} color="rgba(0,0,0,0.04)" />
                        <Controls position="bottom-right" showInteractive={false} />
                        <MiniMap
                            position="bottom-left"
                            pannable={false}
                            zoomable={false}
                            style={{ width: 100, height: 70 }}
                        />
                    </ReactFlow>
                </div>
            )}

            {/* Editable modules accordion — below the flow preview */}
            {isInteractive && accordionItems.length > 0 && (
                <div className="mg-preview-accordion">
                    <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
                        Customize Messages ({accordionItems.length})
                    </Title>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                        This agent allows customization of some of its messages. Expand a message below to edit it for this campaign.
                    </Text>
                    <Collapse
                        size="small"
                        items={accordionItems}
                        bordered={false}
                        className="mg-preview-collapse"
                    />
                </div>
            )}

            {/* Read-only overrides display (campaign viewer) */}
            {!isInteractive && readOnlyOverrideItems.length > 0 && (
                <div className="mg-preview-accordion">
                    <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
                        Message Overrides ({readOnlyOverrideItems.length})
                    </Title>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                        The following messages were customized for this campaign.
                    </Text>
                    <Collapse
                        size="small"
                        items={readOnlyOverrideItems}
                        bordered={false}
                        className="mg-preview-collapse"
                    />
                </div>
            )}

            {overlayOpen && (
                <div className="mg-preview-overlay" onClick={closeOverlay}>
                    <div className="mg-preview-overlay-inner" onClick={(e) => e.stopPropagation()}>
                        <div className="mg-preview-overlay-header">
                            <span className="mg-preview-overlay-title">
                                <RobotOutlined style={{ marginRight: 8 }} />
                                {moduleGroup?.name || 'Agent Flow Preview'}
                            </span>
                            <Button
                                type="text"
                                icon={<CompressOutlined />}
                                onClick={closeOverlay}
                                title="Close"
                            />
                        </div>
                        <div className="mg-preview-overlay-canvas">
                            <ReactFlowProvider>
                                <ReactFlow {...flowProps}>
                                    <Background gap={15} size={1} />
                                    <Controls position="bottom-right" showInteractive={false} />
                                    <MiniMap
                                        position="bottom-left"
                                        pannable
                                        zoomable
                                        style={{ width: 160, height: 110 }}
                                    />
                                </ReactFlow>
                            </ReactFlowProvider>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

// ── Exported wrapper with provider ──
export default function ModuleGroupPreview(props) {
    return (
        <ReactFlowProvider>
            <ModuleGroupPreviewInner {...props} />
        </ReactFlowProvider>
    );
}
